import { SourcingSpendStats } from 'api-clients/monolith';
import {
  createContext,
  Dispatch,
  SetStateAction,
  useContext,
  useState,
} from 'react';

import { EditTargetsDialog } from '@/components/pages/edit-targets';
import {
  HiringGoal,
  OpenEditTargetsDialogParams,
  Targets,
} from '@/components/pages/edit-targets/types';

interface EditTargetsContextType {
  openEditTargetsDialog: (params?: OpenEditTargetsDialogParams) => void;
  closeEditTargetsDialog: () => void;
  targets?: Targets;
  setTargets: Dispatch<SetStateAction<Targets | undefined>>;
  spendStats?: SourcingSpendStats;
  setSpendStats: Dispatch<SetStateAction<SourcingSpendStats | undefined>>;
  hiringGoal?: HiringGoal;
  setHiringGoal: Dispatch<SetStateAction<HiringGoal | undefined>>;
  refetch?: () => Promise<void>;
  setRefetchFn: (refetch: () => Promise<void>) => void;
  hiringGoalUrl?: string;
  setHiringGoalUrl: Dispatch<SetStateAction<string | undefined>>;
}

const EditTargetsContext = createContext<EditTargetsContextType | undefined>(
  undefined,
);

export function EditTargetsProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [targets, setTargets] = useState<Targets>();
  const [spendStats, setSpendStats] = useState<SourcingSpendStats>();
  const [hiringGoal, setHiringGoal] = useState<HiringGoal>();
  const [hiringGoalUrl, setHiringGoalUrl] = useState<string>();
  const [refetch, setRefetchInternal] = useState<() => Promise<void>>();

  const setRefetchFn = (refetchFn: () => Promise<void>) => {
    setRefetchInternal(() => refetchFn);
  };

  const openEditTargetsDialog = (params?: OpenEditTargetsDialogParams) => {
    setTargets(params?.targets);
    setSpendStats(params?.spendStats);
    setIsDialogOpen(true);
  };

  const closeEditTargetsDialog = () => {
    setIsDialogOpen(false);
  };

  return (
    <EditTargetsContext.Provider
      value={{
        openEditTargetsDialog,
        closeEditTargetsDialog,
        targets,
        setTargets,
        spendStats,
        setSpendStats,
        hiringGoal,
        setHiringGoal,
        refetch,
        setRefetchFn,
        hiringGoalUrl,
        setHiringGoalUrl,
      }}
    >
      {children}
      <EditTargetsDialog isOpen={isDialogOpen} setIsOpen={setIsDialogOpen} />
    </EditTargetsContext.Provider>
  );
}

export function useEditTargetsContext() {
  const context = useContext(EditTargetsContext);

  if (!context) {
    throw new Error(
      'useEditTargetsContext must be used within a EditTargetsProvider',
    );
  }

  return context;
}
